import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { EduShell, EmptyState, STUDENT_NAV, TEACHER_NAV } from "@/components/EduShell";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useEducation } from "@/hooks/useEducation";
import { eduRpc } from "@/lib/education";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/notifications")({
  component: NotificationsPage,
  head: () => ({
    meta: [
      { title: "Notifications - Inquira" },
      { name: "description", content: "Class announcements, new assignments, grades returned and deadline reminders." },
      { property: "og:title", content: "Inquira notifications" },
      { property: "og:description", content: "Announcements, new work and returned grades." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface Notification {
  id: string;
  title: string;
  body: string | null;
  kind: string | null;
  read_at: string | null;
  created_at: string;
}

function NotificationsPage() {
  const { user } = useAuth();
  const { isTeacher, isSchoolAdmin } = useEducation();
  const [rows, setRows] = useState<Notification[]>([]);

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("edu_notifications")
      .select("id, title, body, kind, read_at, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(100);
    setRows((data as Notification[]) ?? []);
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  async function markAll() {
    await eduRpc("edu_mark_notifications_read");
    void load();
  }

  const nav = isTeacher || isSchoolAdmin ? TEACHER_NAV : STUDENT_NAV;

  if (!user) {
    return (
      <EduShell nav={STUDENT_NAV} title={i18n.t("edu.notifications.notifications")}>
        <EmptyState title={i18n.t("edu.notifications.sign_in_first")} body="Sign in to see your class updates." />
        <div className="mt-4">
          <Link to="/login" className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><T k="edu.notifications.sign_in" /></Link>
        </div>
      </EduShell>
    );
  }

  return (
    <EduShell
      nav={nav}
      title={i18n.t("edu.notifications.notifications")}
      subtitle={i18n.t("edu.notifications.announcements_new_assignments_returned_g")}
      actions={rows.some((r) => !r.read_at) ? <Button variant="outline" onClick={markAll}><T k="edu.notifications.mark_all_read" /></Button> : undefined}
    >
      {rows.length === 0 ? (
        <EmptyState title={i18n.t("edu.notifications.nothing_new")} body="Class updates will appear here." />
      ) : (
        <ul className="space-y-2">
          {rows.map((n) => (
            <li
              key={n.id}
              className={`rounded-2xl border p-4 ${n.read_at ? "border-rule bg-card" : "border-primary/40 bg-primary/5"}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-ink">{n.title}</span>
                <span className="text-xs text-ink-muted">{new Date(n.created_at).toLocaleString()}</span>
              </div>
              {n.body && <p className="mt-1 text-sm text-ink-muted">{n.body}</p>}
            </li>
          ))}
        </ul>
      )}
    </EduShell>
  );
}
